import type { ProgressBar as ProgressBarProps } from '@web/graphql-components'
import { useQuery } from '@tanstack/react-query'
import { ProgressBar as ProgressBarComponent } from '@web/components/page-components/ProgressBar'
import { usePageStore } from '../../libs/hooks/usePageStore'
import { ProgressBarType } from '@web/components/page-components/ProgressBar/progressBarUtils'
import { useMemo } from 'react'
import { toSearchParams } from 'apps/fe-donation/utils'

const ProgressBar = (component: ProgressBarProps) => {
  const charityId = usePageStore((state) => state.charityId)
  const campaignId = usePageStore((state) => state.campaignId)
  const profileId = usePageStore((state) => state.profileId)
  const isLiveMode = usePageStore((state) => state.isLiveMode)
  const profileData = usePageStore((state) => state.profileData)
  const activeMatchedGiving = usePageStore((state) => state.activeMatchedGiving)
  const isAllPagesLinked = component.isAllPagesLinked ?? true
  const isProfileProgressBar = component.styles?.options?.type === ProgressBarType.Profile
  const isMatchedGivingProgressBar = component.styles?.options?.type === ProgressBarType.MatchedGiving
  const linkedPageIds = component.linkedPageIds ?? []

  const { data: amount = 0 } = useQuery({
    queryKey: [
      'progressBarAmount',
      charityId,
      campaignId,
      linkedPageIds,
      profileId,
      component.styles?.options?.type,
      isMatchedGivingProgressBar ? activeMatchedGiving : null,
    ],
    queryFn: async () => {
      if (isMatchedGivingProgressBar && activeMatchedGiving) {
        const amount = await fetch(
          `/api/matched-giving?${toSearchParams({ campaignId, matchedGivingId: activeMatchedGiving.id, isLiveMode, isFull: 'true' })}`,
        )
          .then((res) => res.json())
          .then((data) => data.amount)
        return amount
      }

      const amount = await fetch(
        `/api/progress-bar?${toSearchParams({
          campaignId,
          isAllPagesLinked,
          pageIds: linkedPageIds,
          isLiveMode,
          profileId: isProfileProgressBar ? profileId : undefined,
        })}`,
      )
        .then((res) => res.json())
        .then((data) => data.amount)
        .catch(() => 0)
      return amount
    },
    refetchOnMount: false,
    enabled: !!charityId && !!campaignId && (isMatchedGivingProgressBar ? !!activeMatchedGiving : true),
    refetchInterval: component?.styles?.options?.isLiveUpdateProgressBar ? 10000 : undefined,
  })

  const goals = useMemo(() => {
    switch (component.styles?.options?.type) {
      case ProgressBarType.MatchedGiving: {
        return (
          activeMatchedGiving?.rounds?.map((round) => (round.endFundRange - round.startFundRange) * round.multiplier) ??
          []
        )
      }
      case ProgressBarType.Profile: {
        return profileId && profileData?.goal ? [profileData.goal] : component.goals
      }
      default: {
        return component.goals
      }
    }
  }, [component.styles?.options?.type, activeMatchedGiving, profileData, profileId])

  // hide matched giving progress bar if not active
  // hide regular progress bar if matched giving is active and hideWhenMatchedGivingActive is true
  if (
    (isMatchedGivingProgressBar && !activeMatchedGiving) ||
    (component.hideWhenMatchedGivingActive && !isMatchedGivingProgressBar && activeMatchedGiving)
  ) {
    return null
  }

  return (
    <ProgressBarComponent
      {...component}
      goals={goals}
      noPreview={false}
      progress={Math.max(0, amount - (component.offset ?? 0))}
    />
  )
}

export default ProgressBar
